import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      
      // Check if user is authenticated before fetching notifications
      const token = localStorage.getItem('authToken');
      if (!token) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      // Set the auth token in the API service
      apiService.setAuthToken(token);
      
      let sessions = [];
      try {
        const response = await apiService.getMyLiveSessions();
        sessions = Array.isArray(response) ? response : [];
      } catch (error) {
        console.warn('Failed to fetch live sessions:', error);
        sessions = [];
      }
      
      // Create notifications for upcoming and live sessions
      const sessionNotifications = [];
      
      // Check for live sessions
      const liveSessions = sessions.filter(session => session.status === 'live');
      liveSessions.forEach(session => {
        sessionNotifications.push({
          id: `live-${session.id}`,
          type: 'live',
          title: `${session.title} is Live Now!`,
          message: `Join ${session.course_name} live session`,
          session: session,
          timestamp: new Date(),
          unread: true,
          priority: 'high'
        });
      });

      // Check for upcoming sessions (within next 30 minutes)
      const upcomingSessions = sessions.filter(session => {
        if (session.status !== 'scheduled' && session.status !== 'draft') return false;
        const timeUntil = new Date(session.scheduled_time) - new Date();
        return timeUntil > 0 && timeUntil <= 30 * 60 * 1000; // 30 minutes
      });

      upcomingSessions.forEach(session => {
        const minutesUntil = Math.floor((new Date(session.scheduled_time) - new Date()) / (1000 * 60));
        sessionNotifications.push({
          id: `upcoming-${session.id}`,
          type: 'upcoming',
          title: `Upcoming Session: ${session.title}`,
          message: `Starts in ${minutesUntil} minutes`,
          session: session,
          timestamp: new Date(),
          unread: true,
          priority: 'medium'
        });
      });

      // Check for sessions starting soon (within next 2 hours)
      const soonSessions = sessions.filter(session => {
        if (session.status !== 'scheduled' && session.status !== 'draft') return false;
        const timeUntil = new Date(session.scheduled_time) - new Date();
        return timeUntil > 30 * 60 * 1000 && timeUntil <= 2 * 60 * 60 * 1000; // 30 min to 2 hours
      });

      soonSessions.forEach(session => {
        const hoursUntil = Math.floor((new Date(session.scheduled_time) - new Date()) / (1000 * 60 * 60));
        sessionNotifications.push({
          id: `soon-${session.id}`,
          type: 'reminder',
          title: `Session Reminder: ${session.title}`,
          message: `Starts in ${hoursUntil} hour${hoursUntil > 1 ? 's' : ''}`,
          session: session,
          timestamp: new Date(),
          unread: true,
          priority: 'low'
        });
      });

      setNotifications(sessionNotifications);
      setUnreadCount(sessionNotifications.length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, unread: false }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, unread: false }))
    );
    setUnreadCount(0);
  };

  // Remove notification
  const removeNotification = (notificationId) => {
    setNotifications(prev => {
      const updated = prev.filter(notif => notif.id !== notificationId);
      setUnreadCount(updated.filter(notif => notif.unread).length);
      return updated;
    });
  };

  // Clear all notifications
  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  // Auto-fetch notifications on mount and every minute
  useEffect(() => {
    fetchNotifications();
    
    const interval = setInterval(fetchNotifications, 60000); // Every minute
    
    return () => clearInterval(interval);
  }, []);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
